import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, Copy, RefreshCw, Check, ChevronDown, ChevronUp, Edit3, MessageSquare, Minimize2, Maximize2, Briefcase, SmilePlus, Shield, Send, User, Bot } from 'lucide-react';
import api from '../utils/api';
import './AIChatPanel.css';
import type {
  Channel,
  Template,
  QuickAction,
  ChatMessage,
  UserMessage,
  AssistantMessage,
  ConfidenceLevel,
} from '../types';


function AIChatPanel(): React.ReactElement {
  // Chat messages history
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [templateCategory, setTemplateCategory] = useState<string>('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const channel: Channel = 'email';

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<number | null>(null); // index of copied message
  const [placeholderValues, setPlaceholderValues] = useState<Record<number, Record<string, string>>>({}); // { msgIndex: { key: value } }
  const [editedDrafts, setEditedDrafts] = useState<Record<number, string>>({}); // { msgIndex: editedText }
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Quick actions
  const quickActions: QuickAction[] = [
    { id: 'rephrase', label: 'Rephrase', icon: MessageSquare, prompt: 'Rephrase the entire draft using different wording while keeping the same meaning and information.' },
    { id: 'shorten', label: 'Shorten', icon: Minimize2, prompt: 'Make this significantly shorter and more concise. Keep only essential information.' },
    { id: 'elaborate', label: 'Elaborate', icon: Maximize2, prompt: 'Expand with more detail, context, and explanations.' },
    { id: 'professional', label: 'More Professional', icon: Briefcase, prompt: 'Rewrite in a more professional and formal business tone.' },
    { id: 'friendly', label: 'More Friendly', icon: SmilePlus, prompt: 'Rewrite in a warmer, friendlier tone while keeping it professional.' },
    { id: 'formal', label: 'Formal', icon: Shield, prompt: 'Rewrite in a strictly formal tone suitable for official communication.' },
  ];

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async (): Promise<void> => {
      try {
        const response = await api.get(`/ai/templates?channel=${channel}`);
        if (response.data.success) {
          setTemplates(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      }
    };
    fetchTemplates();
    setTemplateCategory('');
  }, [channel]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Get last AI draft from messages
  const getLastDraft = (): string | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant' && (msg as AssistantMessage).draft) {
        return (msg as AssistantMessage).draft!;
      }
    }
    return null;
  };

  // Send message
  const handleSend = useCallback(async (overridePrompt?: string): Promise<void> => {
    const prompt = overridePrompt || inputText;
    if (!prompt.trim()) return;

    // Add user message to chat
    const userMsg: UserMessage = { role: 'user', content: prompt.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    if (!overridePrompt) setInputText('');
    setLoading(true);

    try {
      const response = await api.post('/ai/generate-draft', {
        threadId: '__standalone__',
        userPrompt: prompt.trim(),
        templateCategory: templateCategory || null,
        channel
      }, { timeout: 60000 });

      if (response.data.success) {
        const data = response.data.data;
        const aiMsg: AssistantMessage = {
          role: 'assistant',
          draft: data.draft || '',
          confidence: data.confidenceScore || 0,
          reasoning: data.reasoning || '',
          placeholders: data.placeholders || {},
          autoResolvedKeys: new Set(Object.keys(data.autoResolvedFields || {})),
          dbResolvedKeys: new Set(Object.keys(data.dbResolvedFields || {})),
          templateUsed: data.templateUsed || null,
          fewShotCount: data.fewShotCount || 0,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMsg]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant' as const,
          error: response.data.error || 'Failed to generate draft',
          timestamp: new Date()
        }]);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const message = axiosErr.response?.data?.error || axiosErr.message || 'Failed to generate draft';
      setMessages(prev => [...prev, {
        role: 'assistant' as const,
        error: message,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  }, [inputText, templateCategory, channel]);

  // Quick action on last draft
  const handleQuickAction = (action: QuickAction): void => {
    const lastDraft = getLastDraft();
    if (!lastDraft) return;
    const prompt = `Previous draft:\n${lastDraft}\n\nInstruction: ${action.prompt}`;
    handleSend(prompt);
  };

  // Copy resolved draft as rich HTML to clipboard (preserves formatting in Gmail)
  const handleCopy = async (draft: string, index: number, placeholders: Record<string, string>): Promise<void> => {
    try {
      const { copyRichText } = await import('../utils/clipboardUtils');
      const finalText = editedDrafts[index] !== undefined ? editedDrafts[index] : getResolvedDraft(draft, index, placeholders);
      await copyRichText(finalText);
      setCopied(index);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle placeholder value change for a specific message
  const handlePlaceholderChange = (msgIndex: number, key: string, value: string): void => {
    setPlaceholderValues(prev => ({
      ...prev,
      [msgIndex]: { ...(prev[msgIndex] || {}), [key]: value }
    }));
  };

  // Get resolved draft with placeholders filled for a specific message
  const getResolvedDraft = (draft: string, msgIndex: number, placeholders: Record<string, string>): string => {
    let resolved = draft;
    // Use message-level placeholder values
    const values = placeholderValues[msgIndex] || {};
    // Merge with auto-resolved values from the API response
    const merged: Record<string, string> = { ...placeholders, ...values };
    Object.entries(merged).forEach(([key, value]) => {
      if (value && value.trim()) {
        const regex = new RegExp(`\\[${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
        resolved = resolved.replace(regex, value);
      }
    });
    return resolved;
  };

  // Initialize placeholder values when a new AI message arrives with auto-resolved fields
  const initPlaceholders = useCallback((msgIndex: number, placeholders: Record<string, string>): void => {
    if (!placeholderValues[msgIndex] && placeholders && Object.keys(placeholders).length > 0) {
      // Pre-fill with any auto-resolved values from the API
      const autoFilled: Record<string, string> = {};
      Object.entries(placeholders).forEach(([key, value]) => {
        if (value && value.trim()) autoFilled[key] = value;
      });
      if (Object.keys(autoFilled).length > 0) {
        setPlaceholderValues(prev => ({ ...prev, [msgIndex]: { ...(prev[msgIndex] || {}), ...autoFilled } }));
      }
    }
  }, [placeholderValues]);

  // Confidence level helper
  const getConfidenceLevel = (score: number): ConfidenceLevel => {
    if (score >= 0.7) return 'high';
    if (score >= 0.5) return 'medium';
    return 'low';
  };

  return (
    <div className="ai-chat-container">
      {/* Header */}
      <div className="ai-chat-header">
        <div className="ai-chat-header-left">
          <Sparkles size={22} />
          <div>
            <h2>AI Chat</h2>
            <p>Generate emails without thread context</p>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="ai-chat-controls">
{/* Template Selector */}
        <select
          className="ai-chat-template-select"
          value={templateCategory}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTemplateCategory(e.target.value)}
        >
          <option value="">Auto-detect template</option>
          {templates.map(t => (
            <option key={t.category} value={t.category}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Chat Messages */}
      <div className="ai-chat-messages">
        {messages.length === 0 && !loading && (
          <div className="ai-chat-empty">
            <Sparkles size={40} className="ai-chat-empty-icon" />
            <h3>Start a conversation</h3>
            <p>Describe what you need and I'll generate a draft for you. You can paste email context, specify the scenario, or just describe what you want to communicate.</p>
            <div className="ai-chat-suggestions">
              <button onClick={() => { setInputText('Draft a MISA license renewal email for a client whose license expires next month'); }}>
                MISA Renewal
              </button>
              <button onClick={() => { setInputText('Write a welcoming email for a new client who just signed their contract with AstroLabs'); }}>
                Welcome Email
              </button>
              <button onClick={() => { setInputText('Draft a follow-up email about pending GOSI payment for this month'); }}>
                GOSI Payment
              </button>
              <button onClick={() => { setInputText('Write an email explaining the hiring process for a non-KSA expat employee'); }}>
                Hiring Process
              </button>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`ai-chat-msg ${msg.role}`}>
            <div className="ai-chat-msg-avatar">
              {msg.role === 'user' ? <User size={16} /> : <Bot size={16} />}
            </div>
            <div className="ai-chat-msg-body">
              {msg.role === 'user' ? (
                <div className="ai-chat-msg-text">{(msg as UserMessage).content}</div>
              ) : (msg as AssistantMessage).error ? (
                <div className="ai-chat-msg-error">{(msg as AssistantMessage).error}</div>
              ) : (
                <div className="ai-chat-msg-draft">
                  {/* Confidence + Template */}
                  <div className="ai-chat-msg-meta">
                    <span className={`ai-chat-confidence ${getConfidenceLevel((msg as AssistantMessage).confidence || 0)}`}>
                      {Math.round(((msg as AssistantMessage).confidence || 0) * 100)}% confidence
                    </span>
                    {(msg as AssistantMessage).templateUsed && (
                      <span className="ai-chat-template-tag">
                        <Sparkles size={10} /> {(msg as AssistantMessage).templateUsed}
                      </span>
                    )}
                    {((msg as AssistantMessage).fewShotCount || 0) > 0 && (
                      <span className="ai-chat-fewshot-tag">
                        {(msg as AssistantMessage).fewShotCount} examples used
                      </span>
                    )}
                  </div>

                  {/* Reasoning */}
                  {(msg as AssistantMessage).reasoning && (
                    <div className="ai-chat-reasoning">{(msg as AssistantMessage).reasoning}</div>
                  )}

                  {/* Draft with placeholders resolved */}
                  <textarea
                    className="ai-chat-draft-text ai-chat-draft-editable"
                    value={editedDrafts[i] !== undefined ? editedDrafts[i] : getResolvedDraft((msg as AssistantMessage).draft || '', i, (msg as AssistantMessage).placeholders || {})}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedDrafts(prev => ({ ...prev, [i]: e.target.value }))}
                    rows={8}
                  />

                  {/* Placeholder Editor */}
                  {(msg as AssistantMessage).placeholders && Object.keys((msg as AssistantMessage).placeholders || {}).length > 0 && (() => {
                    const assistantMsg = msg as AssistantMessage;
                    initPlaceholders(i, assistantMsg.placeholders || {});
                    const entries = Object.entries(assistantMsg.placeholders || {});
                    const currentVals = placeholderValues[i] || {};
                    const threadKeys: Set<string> = (assistantMsg as any).autoResolvedKeys || new Set();
                    const dbKeys: Set<string> = (assistantMsg as any).dbResolvedKeys || new Set();
                    const merged = entries.map(([key, autoVal]) => ({
                      key,
                      value: currentVals[key] !== undefined ? currentVals[key] : autoVal,
                      isFromThread: threadKeys.has(key),
                      isFromDb: dbKeys.has(key)
                    }));
                    const unfilledCount = merged.filter(p => !p.value || !p.value.trim()).length;

                    return (
                      <div className="ai-chat-placeholders">
                        <div className="ai-chat-placeholders-title">
                          <Edit3 size={13} />
                          {unfilledCount > 0
                            ? `Fill in placeholders (${unfilledCount} remaining)`
                            : 'All placeholders filled'}
                        </div>
                        {merged.map((p) => {
                          const isFilled = p.value && p.value.trim();
                          return (
                            <div key={p.key} className="ai-chat-placeholder-row">
                              <span className="ai-chat-placeholder-label">[{p.key}]</span>
                              <input
                                type="text"
                                className={`ai-chat-placeholder-input ${isFilled ? (p.isFromDb ? 'filled-db' : p.isFromThread ? 'filled-thread' : 'filled') : ''}`}
                                placeholder={`Enter ${p.key}...`}
                                value={p.value || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePlaceholderChange(i, p.key, e.target.value)}
                              />
                              {isFilled && p.isFromDb && <span className="ai-chat-ph-source db">DB</span>}
                              {isFilled && p.isFromThread && !p.isFromDb && <span className="ai-chat-ph-source thread">Thread</span>}
                              {isFilled && !p.isFromDb && !p.isFromThread && <Check size={13} className="ai-chat-placeholder-check" />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Copy button */}
                  <div className="ai-chat-msg-actions">
                    <button
                      className={`ai-chat-copy-btn ${copied === i ? 'copied' : ''}`}
                      onClick={() => handleCopy((msg as AssistantMessage).draft || '', i, (msg as AssistantMessage).placeholders || {})}
                    >
                      {copied === i ? <Check size={13} /> : <Copy size={13} />}
                      {copied === i ? 'Copied!' : 'Copy to Clipboard'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div className="ai-chat-msg assistant">
            <div className="ai-chat-msg-avatar"><Bot size={16} /></div>
            <div className="ai-chat-msg-body">
              <div className="ai-chat-typing">
                <div className="ai-chat-typing-dot"></div>
                <div className="ai-chat-typing-dot"></div>
                <div className="ai-chat-typing-dot"></div>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Quick Actions (show when there's a draft) */}
      {getLastDraft() && !loading && (
        <div className="ai-chat-quick-bar">
          {quickActions.map((action) => {
            const IconComponent = action.icon;
            return (
              <button
                key={action.id}
                className="ai-quick-chip"
                onClick={() => handleQuickAction(action)}
                disabled={loading}
              >
                <IconComponent size={12} />
                {action.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Input Area */}
      <div className="ai-chat-input-area">
        <textarea
          className="ai-chat-input"
          placeholder="Describe what you'd like to draft, or paste email context here..."
          value={inputText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputText(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={2}
        />
        <button
          className="ai-chat-send-btn"
          onClick={() => handleSend()}
          disabled={loading || !inputText.trim()}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

export default AIChatPanel;
